import React, { useState, useEffect } from "react";
import TechSoftFooter from "../../components/common/TechSoftFooter";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { atualizarPrefeitura, buscarPrefeituraPorId } from "../../services/prefeitura";

interface PrefeituraData {
  id: number;
  nome: string;
  email: string;
  telefone?: string;
  whatsapp?: string;
}

// Função para formatar Telefone/WhatsApp
const formatarTelefone = (value: string): string => {
  const numbers = value.replace(/\D/g, "");

  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 6) {
    return numbers.replace(/^(\d{2})(\d{0,4})$/, "($1) $2");
  } else if (numbers.length <= 10) {
    // Telefone fixo: (XX) XXXX-XXXX
    return numbers.replace(/^(\d{2})(\d{4})(\d{0,4})$/, "($1) $2-$3");
  } else {
    // Celular: (XX) XXXXX-XXXX
    return numbers.slice(0, 11).replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  }
};

interface AlterarEmailForm {
  emailAtual: string;
  novoEmail: string;
  confirmarEmail: string;
}

interface AlterarSenhaForm {
  senhaAtual: string;
  novaSenha: string;
  confirmarSenha: string;
}

const ConfiguracoesPrefeitura: React.FC = () => {
  const navigate = useNavigate();
  const [prefeitura, setPrefeitura] = useState<PrefeituraData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error" | "";
    text: string;
  }>({
    type: "",
    text: "",
  });

  // Estados para campos editáveis da prefeitura
  const [nomePrefeitura, setNomePrefeitura] = useState("");
  const [telefonePrefeitura, setTelefonePrefeitura] = useState("");
  const [whatsappPrefeitura, setWhatsappPrefeitura] = useState("");

  // Estados para formulários
  const [emailForm, setEmailForm] = useState<AlterarEmailForm>({
    emailAtual: "",
    novoEmail: "",
    confirmarEmail: "",
  });

  const [senhaForm, setSenhaForm] = useState<AlterarSenhaForm>({
    senhaAtual: "",
    novaSenha: "",
    confirmarSenha: "",
  });

  // Estados para mostrar/ocultar senhas
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  // Carregar dados da prefeitura do localStorage
  useEffect(() => {
    const carregarDadosPrefeitura = async () => {
      const prefeituraId = localStorage.getItem("prefeituraId") || sessionStorage.getItem("prefeituraId");
      const prefeituraNome = localStorage.getItem("prefeituraNome") || sessionStorage.getItem("prefeituraNome");
      const prefeituraEmail = localStorage.getItem("prefeituraEmail") || sessionStorage.getItem("prefeituraEmail");
      const prefeituraTelefone = localStorage.getItem("prefeituraTelefone") || sessionStorage.getItem("prefeituraTelefone");
      const prefeituraWhatsapp = localStorage.getItem("prefeituraWhatsapp") || sessionStorage.getItem("prefeituraWhatsapp");

      if (prefeituraId && prefeituraNome && prefeituraEmail) {
        const dadosCompletos: PrefeituraData = {
          id: parseInt(prefeituraId),
          nome: prefeituraNome,
          email: prefeituraEmail,
          telefone: prefeituraTelefone || "",
          whatsapp: prefeituraWhatsapp || "",
        };

        setPrefeitura(dadosCompletos);
        setNomePrefeitura(prefeituraNome);
        setTelefonePrefeitura(formatarTelefone(prefeituraTelefone || ""));
        setWhatsappPrefeitura(formatarTelefone(prefeituraWhatsapp || ""));
        setEmailForm((prev: AlterarEmailForm) => ({ ...prev, emailAtual: prefeituraEmail }));

        // Buscar dados atualizados do backend
        try {
          const response = await buscarPrefeituraPorId(prefeituraId);
          if (response.data) {
            const dadosAtualizados = response.data;
            setPrefeitura({
              id: dadosAtualizados.id,
              nome: dadosAtualizados.nome,
              email: dadosAtualizados.email,
              telefone: dadosAtualizados.telefone || "",
              whatsapp: dadosAtualizados.whatsapp || "",
            });
            setNomePrefeitura(dadosAtualizados.nome);
            setTelefonePrefeitura(formatarTelefone(dadosAtualizados.telefone || ""));
            setWhatsappPrefeitura(formatarTelefone(dadosAtualizados.whatsapp || ""));
            setEmailForm((prev: AlterarEmailForm) => ({ ...prev, emailAtual: dadosAtualizados.email }));
          }
        } catch (error) {
          console.log("Usando dados do localStorage - erro ao buscar do backend:", error);
        }
      } else {
        setMessage({
          type: "error",
          text: "Dados da prefeitura não encontrados. Faça login novamente.",
        });
      }
    };

    carregarDadosPrefeitura();
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEmailForm((prev: AlterarEmailForm) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSenhaForm((prev: AlterarSenhaForm) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSalvarAlteracoes = async () => {
    if (!prefeitura) {
      setMessage({ type: "error", text: "Dados da prefeitura não encontrados." });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // Validações
      if (!nomePrefeitura || nomePrefeitura.trim() === "") {
        setMessage({ type: "error", text: "O nome da prefeitura não pode estar vazio." });
        setLoading(false);
        return;
      }

      // Se estiver alterando email
      if (emailForm.novoEmail || emailForm.confirmarEmail) {
        if (emailForm.novoEmail !== emailForm.confirmarEmail) {
          setMessage({ type: "error", text: "Os emails não coincidem." });
          setLoading(false);
          return;
        }

        if (emailForm.novoEmail === emailForm.emailAtual) {
          setMessage({
            type: "error",
            text: "O novo email deve ser diferente do atual.",
          });
          setLoading(false);
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailForm.novoEmail)) {
          setMessage({ type: "error", text: "Por favor, digite um email válido." });
          setLoading(false);
          return;
        }
      }

      // Se estiver alterando senha
      if (senhaForm.novaSenha || senhaForm.confirmarSenha) {
        if (!senhaForm.senhaAtual) {
          setMessage({
            type: "error",
            text: "Digite a senha atual para alterar a senha.",
          });
          setLoading(false);
          return;
        }

        if (senhaForm.novaSenha !== senhaForm.confirmarSenha) {
          setMessage({ type: "error", text: "As senhas não coincidem." });
          setLoading(false);
          return;
        }

        if (senhaForm.novaSenha.length < 6) {
          setMessage({
            type: "error",
            text: "A nova senha deve ter pelo menos 6 caracteres.",
          });
          setLoading(false);
          return;
        }

        if (senhaForm.novaSenha === senhaForm.senhaAtual) {
          setMessage({
            type: "error",
            text: "A nova senha deve ser diferente da atual.",
          });
          setLoading(false);
          return;
        }
      }

      // Validar telefones
      const telefoneNumerico = telefonePrefeitura.replace(/\D/g, "");
      const whatsappNumerico = whatsappPrefeitura.replace(/\D/g, "");

      if (telefoneNumerico && telefoneNumerico.length < 10) {
        setMessage({
          type: "error",
          text: "Telefone inválido. Deve ter pelo menos 10 dígitos.",
        });
        setLoading(false);
        return;
      }

      if (whatsappNumerico && whatsappNumerico.length < 10) {
        setMessage({
          type: "error",
          text: "WhatsApp inválido. Deve ter pelo menos 10 dígitos.",
        });
        setLoading(false);
        return;
      }

      const dadosAtualizacao: any = {
        nome: nomePrefeitura.trim(),
        telefone: telefoneNumerico || "",
        whatsapp: whatsappNumerico || "",
      };

      // Adicionar email se estiver alterando
      if (emailForm.novoEmail && emailForm.novoEmail !== emailForm.emailAtual) {
        dadosAtualizacao.email = emailForm.novoEmail;
      }

      // Adicionar senha se estiver alterando
      if (senhaForm.novaSenha && senhaForm.senhaAtual) {
        dadosAtualizacao.senha = senhaForm.novaSenha;
      }

      // Chamar API de atualização
      await atualizarPrefeitura(prefeitura.id, dadosAtualizacao);

      // Atualizar localStorage
      localStorage.setItem("prefeituraNome", nomePrefeitura.trim());
      localStorage.setItem("prefeituraTelefone", telefoneNumerico || "");
      localStorage.setItem("prefeituraWhatsapp", whatsappNumerico || "");
      if (emailForm.novoEmail) {
        localStorage.setItem("prefeituraEmail", emailForm.novoEmail);
      }

      // Atualizar estado local
      setPrefeitura({
        ...prefeitura,
        nome: nomePrefeitura.trim(),
        telefone: telefoneNumerico || "",
        whatsapp: whatsappNumerico || "",
        email: emailForm.novoEmail || prefeitura.email,
      });

      // Limpar campos de senha e email
      if (emailForm.novoEmail) {
        setEmailForm({
          emailAtual: emailForm.novoEmail,
          novoEmail: "",
          confirmarEmail: "",
        });
      }

      setSenhaForm({
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: "",
      });

      setMessage({
        type: "success",
        text: "Alterações salvas com sucesso!",
      });
    } catch (error: any) {
      console.error("Erro ao salvar alterações:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Erro ao salvar alterações. Tente novamente.";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!prefeitura) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Dados da prefeitura não encontrados.
          <Button onClick={() => navigate("/login-prefeitura")} sx={{ ml: 2 }}>
            Fazer Login
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <SettingsIcon sx={{ fontSize: 32, mr: 2, color: "primary.main" }} />
          <Typography variant="h5" component="h1" fontWeight="600">
            Configurações da Prefeitura
          </Typography>
        </Box>

        {/* Informações da Prefeitura */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Informações da Prefeitura"
            titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              <TextField
                label="Nome da Prefeitura"
                value={nomePrefeitura}
                onChange={(e) => setNomePrefeitura(e.target.value)}
                required
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Email"
                value={emailForm.emailAtual}
                disabled
                fullWidth
                variant="outlined"
                helperText="Para alterar o email, preencha os campos abaixo"
              />
              <TextField
                label="Telefone"
                value={telefonePrefeitura}
                onChange={(e) => setTelefonePrefeitura(formatarTelefone(e.target.value))}
                fullWidth
                variant="outlined"
                placeholder="(51) 3333-3333"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="WhatsApp"
                value={whatsappPrefeitura}
                onChange={(e) => setWhatsappPrefeitura(formatarTelefone(e.target.value))}
                fullWidth
                variant="outlined"
                placeholder="(51) 99999-9999"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {message.text && (
          <Alert severity={message.type as any} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {/* Alterar Email */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="Alterar Email"
                avatar={<EmailIcon color="primary" />}
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Email Atual"
                    name="emailAtual"
                    type="email"
                    value={emailForm.emailAtual}
                    disabled
                    fullWidth
                    variant="outlined"
                  />

                  <TextField
                    label="Novo Email"
                    name="novoEmail"
                    type="email"
                    value={emailForm.novoEmail}
                    onChange={handleEmailChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Digite o novo email"
                  />

                  <TextField
                    label="Confirmar Novo Email"
                    name="confirmarEmail"
                    type="email"
                    value={emailForm.confirmarEmail}
                    onChange={handleEmailChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Confirme o novo email"
                    error={
                      emailForm.confirmarEmail !== "" &&
                      emailForm.novoEmail !== emailForm.confirmarEmail
                    }
                    helperText={
                      emailForm.confirmarEmail !== "" &&
                      emailForm.novoEmail !== emailForm.confirmarEmail
                        ? "Os emails não coincidem"
                        : "Deixe em branco para manter o email atual"
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Alterar Senha */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="Alterar Senha"
                avatar={<LockIcon color="primary" />}
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Senha Atual"
                    name="senhaAtual"
                    type={mostrarSenhaAtual ? "text" : "password"}
                    value={senhaForm.senhaAtual}
                    onChange={handleSenhaChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Digite a senha atual"
                    helperText="Necessário para alterar a senha"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setMostrarSenhaAtual(!mostrarSenhaAtual)
                            }
                            edge="end"
                          >
                            {mostrarSenhaAtual ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Nova Senha"
                    name="novaSenha"
                    type={mostrarNovaSenha ? "text" : "password"}
                    value={senhaForm.novaSenha}
                    onChange={handleSenhaChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Digite a nova senha"
                    inputProps={{ minLength: 6 }}
                    helperText="Mínimo 6 caracteres"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setMostrarNovaSenha(!mostrarNovaSenha)
                            }
                            edge="end"
                          >
                            {mostrarNovaSenha ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirmar Nova Senha"
                    name="confirmarSenha"
                    type={mostrarConfirmarSenha ? "text" : "password"}
                    value={senhaForm.confirmarSenha}
                    onChange={handleSenhaChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Confirme a nova senha"
                    error={
                      senhaForm.confirmarSenha !== "" &&
                      senhaForm.novaSenha !== senhaForm.confirmarSenha
                    }
                    helperText={
                      senhaForm.confirmarSenha !== "" &&
                      senhaForm.novaSenha !== senhaForm.confirmarSenha
                        ? "As senhas não coincidem"
                        : "Deixe em branco para manter a senha atual"
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setMostrarConfirmarSenha(!mostrarConfirmarSenha)
                            }
                            edge="end"
                          >
                            {mostrarConfirmarSenha ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Botão Salvar Alterações */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={<SaveIcon />}
            onClick={handleSalvarAlteracoes}
            sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
          >
            {loading ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          mt: 3,
          pb: 2,
        }}
      >
        <TechSoftFooter />
      </Box>
    </Container>
  );
};

export default ConfiguracoesPrefeitura;

